import { useEffect, useMemo, useState } from 'react';
import { toSpecialFixed } from '@/lib/misc/utils';
import { BuySellAmountInputProps, BuySellAssetControllerStages } from '@shared/types/frontend/assetPage';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import Keypad from '@/components/asset/sendFunds/keypad/Keypad';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MobileSelectFromToAsset from './MobileSelectFromToAsset';
import classNames from '@/lib/misc/classNames';
import { centerAmount } from '@/lib/misc/formatInputAmount';
import { useTouchHandlerName } from '@/hooks/utilities/use-mobile';
const MobileAmountKeypad = ({
  form,
  maxBalance,
  tradeSide,
  tokenSymbol,
  handleAmountChange,
  setStage
}: BuySellAmountInputProps) => {
  maxBalance = typeof maxBalance === 'string' ? maxBalance.replace(',', '') : maxBalance;
  const [selectedToggleAmount, setSelectedToggleAmount] = useState<string>(form.getValues('tradeAmount'));
  //hack to reset can't deal with this rn
  useEffect(() => {
    setSelectedToggleAmount(null);
  }, []);
  const _handleToggle = (percentage: string) => {
    console.log('BuySellAmountInput:percentagehandleToggle:', {
      maxBalance,
      percentage
    });
    setSelectedToggleAmount(percentage);
    const amount = percentage === 'ALL' ? maxBalance : (Number(maxBalance) * (Number(percentage === 'ALL' ? '100' : percentage) / 100)).toString();
    // console.log('BuySellAmountInput:_handleToggle:', { maxBalance, percentage });

    handleAmountChange(amount);
  };
  const handleSetInput = e => {
    let _value = e.target.value;
    // Allow only numbers and a dot with restrictions
    if (/^(0|[1-9][0-9]*)(\.[0-9]*)?$/.test(_value) || _value === '') {
      handleAmountChange(_value);
      // setSelectedToggleAmount(_value);
    }
  };
  const handleNumberInput = (val: string) => {
    let _returnedValue = val;
    if (!val || isNaN(parseFloat(val))) {
      _returnedValue = '0';
    }
    if (val?.includes('.')) {
      let parts = val.split('.');
      let integerPart = parts[0];
      let decimalPart = parts[1];
      let decimalLength = decimalPart.length;
      if (decimalLength > 4) {
        decimalPart = decimalPart.substring(0, 4);
      }
      _returnedValue = `${integerPart}.${decimalPart}`;
    }
    return _returnedValue;
  };
  const handleNumPadUsdChange = (val: string) => {
    const tokenUsdAmount = handleNumberInput(val);
    form.setValue('tradeAmount', tokenUsdAmount);
  };
  const touchHandlerName = useTouchHandlerName();
  return <div className='grid gap-8 w-full mt-4' data-sentry-component="MobileAmountKeypad" data-sentry-source-file="MobileAmountKeypad.tsx">
      <FormField control={form.control} name='tradeAmount' render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className='flex flex-col justify-center items-center w-full'>
                <div className='w-full h-[40px] relative'>
                  <div className='text-genius-cream break-all w-full'>
                    <div className='w-full'>
                      <div className={`w-full justify-center items-center transition-all`}>
                        <div className={`flex justify-center w-full items-center 
                                                    absolute top-1/2 transform -translate-y-1/2
                                                     ${centerAmount(form.getValues('tradeAmount'))}`}>
                          <Input {...field} type='text' onChange={handleSetInput} className={`p-0 m-[0px] ta-r w-[50%] bg-transparent border-none focus:outline-none focus-visible:outline-none text-genius-cream`} placeholder='0' />
                          <span className='text-genius-cream/70 w-[50%]'>USD</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FormControl>
          </FormItem>} data-sentry-element="FormField" data-sentry-source-file="MobileAmountKeypad.tsx" />
      {/* select from/to token */}
      <MobileSelectFromToAsset form={form} setStage={setStage} data-sentry-element="MobileSelectFromToAsset" data-sentry-source-file="MobileAmountKeypad.tsx" />
      {/* available balance */}
      <div className='flex flex-nowrap justify-center items-center gap-1 text-[12px]'>
        <>
          {maxBalance ? <>
              {' '}
              {tradeSide === 'sell' ? <>
                  <p>${toSpecialFixed(maxBalance).value}</p>
                  <p className='opacity-40'>Of</p>
                  <p className='opacity-40'>{tokenSymbol}</p>
                </> : <p>${toSpecialFixed(maxBalance).value}</p>}
              <p className='opacity-40'>Available</p>
            </> : <p className='opacity-50'>
              {tradeSide === 'buy' ? 'Select From Token' : `No balance on ${tokenSymbol}`}
            </p>}
        </>
      </div>
      {/* toggle options */}
      <div className='flex items-center justify-center gap-4'>
        <ToggleGroup type='single' value={selectedToggleAmount} onValueChange={_handleToggle} className='gap-4' data-sentry-element="ToggleGroup" data-sentry-source-file="MobileAmountKeypad.tsx">
          {['10', '25', '50', 'ALL'].map(value => <ToggleGroupItem key={value} value={value} disabled={value === selectedToggleAmount || !maxBalance} className={classNames(`bg-genius-blue text-genius-cream
          rounded-full h-[31px] w-[61px] text-[14px] data-[state=on]:opacity-100 `, selectedToggleAmount === value ? 'bg-genius-pink text-genius-blue opacity-100' : 'bg-genius-blue text-genius-cream opacity-100')}>
              {value === 'ALL' ? value : `${value}%`}
            </ToggleGroupItem>)}
        </ToggleGroup>
        <Button type='button' size='icon' variant='outline' className='rounded-full w-[31px] h-[31px] text-genius-cream border-genius-blue hover:bg-genius-blue' {...{
        [touchHandlerName]: () => {
          setStage(BuySellAssetControllerStages.slippageConfig);
        }
      }} data-sentry-element="Button" data-sentry-source-file="MobileAmountKeypad.tsx">
          <Settings className='w-[16px]' data-sentry-element="Settings" data-sentry-source-file="MobileAmountKeypad.tsx" />
        </Button>
      </div>
      {/* keypad */}
      <Keypad handleNumPadUsdChange={handleNumPadUsdChange} handleNumPadTokenChange={() => {}} isSwitched={false} usdValue={form.watch('tradeAmount')} tokenAmount='' disablePads={false} data-sentry-element="Keypad" data-sentry-source-file="MobileAmountKeypad.tsx" />
    </div>;
};
export default MobileAmountKeypad;